import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { iconTileColors } from "/imports/ui/components/discovery";
import { formatExpiry, formatDate } from "../packagesHelpers";

// "You own this" summary line: what the client still holds of this variant.
function ownedText(owned) {
  const sessions = `${owned.sessionsRemaining} session${owned.sessionsRemaining === 1 ? "" : "s"} left`;
  if (owned.noExpiry) return `${sessions} · No expiry`;
  if (owned.expiryDate) return `${sessions} · Expires ${formatDate(owned.expiryDate)}`;
  return sessions;
}

function VariantRow({ pkg, opt, currency, onBuy, isBuying, memberPricing, locationLabel, owned, highlighted }) {
  // A used-up pack is history, not something the client holds — no highlight.
  const ownsSessions = !!owned && owned.sessionsRemaining > 0;
  const price = Number.isFinite(opt.price) ? opt.price : parseFloat(opt.price) || 0;
  const memberPrice = Number.isFinite(opt.memberPrice) ? opt.memberPrice : parseFloat(opt.memberPrice) || 0;
  const hasMemberDiscount = memberPrice > 0 && memberPrice < price;
  // When the client's memberships cover everywhere this pack can be spent, the
  // server will actually charge memberPrice — surface that as the displayed price.
  const memberPricingActive = !!memberPricing?.applies && hasMemberDiscount;
  const expiry = formatExpiry(opt);
  const variantLabel = opt.sessions ? `${opt.sessions} session${opt.sessions === 1 ? "" : "s"}` : "Package";

  // Per-session economics for the "you save $X per session" explainer.
  const pricePerSession = Number.isFinite(opt.pricePerSession) ? opt.pricePerSession : parseFloat(opt.pricePerSession) || null;
  const regularSessionPrice = Number.isFinite(opt.regularSessionPrice) ? opt.regularSessionPrice : parseFloat(opt.regularSessionPrice) || null;
  const sessionSaving = Number.isFinite(opt.sessionSaving) ? opt.sessionSaving : parseFloat(opt.sessionSaving) || null;
  const hasSaving = sessionSaving > 0 && regularSessionPrice && pricePerSession != null;
  // The whole pack's saving reads at a glance; "save 25" alone was taken for the
  // total and undersold the pack.
  const totalSaving = hasSaving && opt.sessions > 1 ? sessionSaving * opt.sessions : null;

  return (
    <Card
      variant="outlined"
      sx={{
        ...(ownsSessions && {
          borderColor: "primary.main",
          borderWidth: 1.5,
          backgroundColor: (theme) => `${theme.palette.primary.main}0A`
        }),
        ...(highlighted && {
          borderColor: "primary.main",
          borderWidth: 2,
          boxShadow: (theme) => `0 0 0 0.25rem ${theme.palette.primary.main}26`
        })
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 1.5, sm: 2 },
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 2.5 } }
        }}
      >
        <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box
            sx={[
              {
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              },
              (t) => iconTileColors(t, 0.08)
            ]}
          >
            <LocalActivityOutlinedIcon sx={{ fontSize: "1.375rem" }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <LocalActivityOutlinedIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "1.375rem", color: "primary.main", flexShrink: 0 }} />
            {/* The duration is what distinguishes same-named variants, so it
                belongs in the scan anchor (title), not buried in the meta line. */}
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: { xs: "-webkit-box", sm: "block" },
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: { xs: 2, sm: "unset" },
                whiteSpace: { xs: "normal", sm: "nowrap" }
              }}
            >
              {pkg.name}
              {opt.sessionDuration ? ` · ${opt.sessionDuration} min` : ""}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {variantLabel}
            {expiry ? ` · ${opt.noExpiry ? expiry : `use within ${expiry}`}` : ""}
          </Typography>
          {(opt.upfrontBooking === "all" || opt.upfrontBooking === "partial") && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "text.secondary", fontWeight: 600 }}>
              {opt.upfrontBooking === "all" ? "You pick all your session dates when you buy" : "You pick your first session date(s) when you buy"}
            </Typography>
          )}
          {hasSaving && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                  {currency} {pricePerSession.toFixed(2)} a session
                </Box>{" "}
                — normally {currency} {regularSessionPrice.toFixed(2)}
              </Typography>
              <Chip
                size="small"
                label={
                  totalSaving
                    ? `You save ${currency} ${totalSaving.toFixed(2)} on ${opt.sessions} sessions`
                    : `You save ${currency} ${sessionSaving.toFixed(2)} a session`
                }
                sx={{
                  mt: 0.5,
                  height: "1.25rem",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  color: "primary.main",
                  backgroundColor: (theme) => `${theme.palette.primary.main}1F`
                }}
              />
            </Box>
          )}
          {!hasSaving && pricePerSession && opt.sessions > 1 && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              That's {currency} {pricePerSession.toFixed(2)} per session
            </Typography>
          )}
          {locationLabel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <PlaceOutlinedIcon sx={{ fontSize: "0.875rem", color: "text.secondary" }} />
              <Typography variant="caption" color="textSecondary">
                {locationLabel}
              </Typography>
            </Box>
          )}
          {pkg.description && (
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.5 }}>
              {pkg.description}
            </Typography>
          )}
          {ownsSessions && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: "0.9375rem", color: "primary.main" }} />
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700 }}>
                You own this pack — {ownedText(owned)}
              </Typography>
            </Box>
          )}
          {owned && !ownsSessions && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
              <ErrorOutlineIcon sx={{ fontSize: "0.9375rem", color: "error.main" }} />
              <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700 }}>
                Used up — buy again to keep booking
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            ml: { sm: "auto" },
            flexBasis: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
            mt: { xs: 0.5, sm: 0 },
            pt: { xs: 1.5, sm: 0 },
            borderTop: { xs: "1px solid", sm: "none" },
            borderTopColor: { xs: "divider" }
          }}
        >
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            {memberPricingActive ? (
              <>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ lineHeight: 1.2, color: "primary.main", fontSize: { xs: "1.0625rem", sm: "1.25rem" } }}
                >
                  {currency} {memberPrice.toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", textDecoration: "line-through", fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}
                >
                  {currency} {price.toFixed(2)}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: "1.0625rem", sm: "1.25rem" } }}>
                  {currency} {price.toFixed(2)}
                </Typography>
                {hasMemberDiscount && (
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                      gap: 0.375,
                      fontSize: { xs: "0.6875rem", sm: "0.75rem" }
                    }}
                  >
                    <WorkspacePremiumOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                    Members {currency} {memberPrice.toFixed(2)}
                  </Typography>
                )}
                {/* A member at only some of the pack's branches: the condition on
                    the member price, under it — orange, since it isn't theirs yet. */}
                {memberPricing?.note && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      maxWidth: { sm: "13rem" },
                      fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: memberPricing.note.warning ? "warning.dark" : "primary.main"
                    }}
                  >
                    {memberPricing.note.text}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={isBuying}
            onClick={() => onBuy(pkg, opt)}
            sx={{ textTransform: "none", color: "common.white", minWidth: "5.625rem" }}
          >
            {isBuying ? <CircularProgress size={18} /> : "Buy"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default VariantRow;
